import { navigation } from '../app/navigation.js';
import { profile } from '../data/profile.js';
import { PanelBase } from '../ui/panel_base.js';
import { listenToEvent } from '../util/listen_to_event.js';
import { MessageBox } from './message_box.js';
import { showChangeAvatarDialog } from './change_avatar_dialog.js';

class ProfilePanel extends PanelBase {
  static id = navigation.kProfilePanelId;

  initialize() {
    super.initialize();

    // Pipe UI.
    profile.pipe({
      avatar: v => (this.avatarImg.src = v),
      email: v => (this.emailText.textContent = v),
      name: v => (this.nameTextfield.textContent = v),
    });

    // Name change.
    listenToEvent(this.nameChangeCancelButton, 'click', () => {
      this.nameTextfield.textContent = profile.name;
      this.nameContainer.removeAttribute('value-changed');
    });

    listenToEvent(this.nameChangeAcceptButton, 'click', () => {
      this.nameContainer.removeAttribute('value-changed');
      this.changeName();
    });

    listenToEvent(this.nameTextfield, 'input', () => {
      let isChanged = this.nameTextfield.textContent != profile.name;
      isChanged
        ? this.nameContainer.setAttribute('value-changed', 'true')
        : this.nameContainer.removeAttribute('value-changed');
      this.pwdContainer.open = false;
    });

    // Password change.
    listenToEvent(this.pwdContainer, 'toggle', () => {
      if (this.pwdContainer.open) {
        this.pwdChangeAcceptButton.scrollIntoView();
      }
    });

    listenToEvent(this.pwdForm, 'submit', () => this.changePassword(), {
      preventDefault: true,
    });

    // Avatar change.
    document
      .getElementById('profile-avatar-file')
      .addEventListener('change', ev => this.changeAvatar(ev.target.files[0]));

    // Messages.
    this.msgBox = new MessageBox(document.getElementById('panel-name-msg'));
  }

  static markup = `
    <div id='panel-name-msg' class='msg' hidden></div>
    <div class='profile-container'>
      <div class='profile-avatar'>
        <img id='profile-avatar-pic'>
        <div class='profile-avatar-link-container'>
          <label>
            <input id='profile-avatar-file' type='file' accept='image/*' hidden>
            <span class='link'>Change avatar</span>
          </label>
        </div>
      </div>
      <div class='profile-account-container'>
        <div class='profile-name-container'>
          <div id='profile-name' class='light-grouping' contentEditable> </div>
          <div id='profile-email' class='email'></div>
          <div class='dialog-buttons'>
            <div class='dialog-button dialog-accept'>Save</div>
            <div class='dialog-button dialog-cancel'>Cancel</div>
          </div>
        </div>
        <details class='profile-pwd-container'>
          <summary class='list-heading'>
            <span class='list-heading-text'>Change password</span>
          </summary>
          <form class='default'>
            <input id='panel-pwd-old' type='password'
                   autocomplete='current-password'
                   placeholder='Old password' required>
            <input id='panel-pwd-new' type='password'
                   autocomplete='new-password'
                   placeholder='New password' required>
            <div class='dialog-buttons'>
              <input type='submit' hidden=''>
              <div class='dialog-button dialog-accept'
                   tabindex='1'
                   onclick='this.previousElementSibling.click();'>Change</div>
            </div>
          </form>
        </details>
      </div>
    </div>
  `;

  static get selectors() {
    const NameContainer = '.profile-name-container';
    const PwdContainer = '.profile-pwd-container';
    return Object.assign({}, super.selectors, {
      AvatarImg: '#profile-avatar-pic',
      EmailText: '#profile-email',
      NameChangeAcceptButton: `${NameContainer} .dialog-accept`,
      NameChangeCancelButton: `${NameContainer} .dialog-cancel`,
      NameContainer,
      NameTextfield: `${NameContainer} #profile-name`,
      OldPwdInput: `${PwdContainer} #panel-pwd-old`,
      PwdChangeAcceptButton: `${PwdContainer} .dialog-accept`,
      PwdContainer,
      PwdForm: `${PwdContainer} form`,
      PwdInput: `${PwdContainer} #panel-pwd-new`,
    });
  }

  static get elements() {
    let s = this.selectors;
    return {
      avatarImg: s.AvatarImg,
      emailText: s.EmailText,
      nameChangeAcceptButton: s.NameChangeAcceptButton,
      nameChangeCancelButton: s.NameChangeCancelButton,
      nameContainer: s.NameContainer,
      nameTextfield: s.NameTextfield,
      oldPwdInput: s.OldPwdInput,
      pwdChangeAcceptButton: s.PwdChangeAcceptButton,
      pwdContainer: s.PwdContainer,
      pwdForm: s.PwdForm,
      pwdInput: s.PwdInput,
    };
  }

  get historyState() {
    return [{ page: this.page }, `Profile | CamperAid`, ''];
  }

  async changeName() {
    let name = this.nameTextfield.textContent;
    let answer = await profile.setName(name);
    if (answer && answer.errorCode) {
      this.msgBox.unexpectedError();
    }
  }

  async changePassword() {
    let old_pwd = this.oldPwdInput.value;
    let pwd = this.pwdInput.value;
    let answer = await profile.setPassword({ old_pwd, pwd });
    if (answer && answer.errorCode) {
      switch (answer.errorCode) {
        case 'BAD_CREDENTIALS':
          this.msgBox.error(`Incorrect password`);
          break;

        default:
          this.msgBox.unexpectedError();
          break;
      }
      return;
    }

    this.msgBox.success(`Password changed`);
    this.pwdForm.reset();
    this.pwdContainer.open = false;
  }

  async changeAvatar(blob) {
    let avatar = await showChangeAvatarDialog(window.URL.createObjectURL(blob));
    return avatar && profile.setAvatar(avatar);
  }
}

navigation.registerPanel(ProfilePanel);

export { ProfilePanel };
